import React, { useState, useEffect } from 'react';
import environment from 'environments';
import apiService from 'app/bff/services/apiService';
import { LinkPositionEnum, FooterLinkIdEnum, LinkTypesEnum } from 'app/bff/enums/link';
import { LinkInfoResp } from 'app/bff/models/info/infoGetlinkUrl';
import commonService from 'app/core/services/commonService';
import modalService from 'app/core/services/modalService';
import { ModalNamesEnum } from 'app/core/enums/ui/modals';
import { useSelector } from 'react-redux';
import { RootState } from 'app/store/types';
import { SystemParamIdEnum } from 'app/bff/enums/system';
import useUIEffect from 'app/core/hooks/useUIEffect';

declare const $: any;

const Footer: React.FC = () => {
  const [footerState, setFooterState] = useState<LinkInfoResp[]>([]);
  const footerNavs = footerState.filter((footer) => footer.type === '0');
  const footerInfoLinks = footerState.filter((footer) => footer.type !== '0' && !!footer.link);
  const footerInfoText = footerState.filter((footer) => footer.type !== '0' && !footer.link);
  const commonBaseUrlsState = useSelector((state: RootState) => state.common.baseUrls);
  const authorization = useSelector((state: RootState) => state.common.member.authorization);
  const isModalShown = useUIEffect();

  useEffect(() => {
    if (footerState.length > 0) commonService.appendScriptResource(`${environment.application.homepage}/js/footer.js`);
  }, [footerState]);

  useEffect(() => {
    (async () => {
      const response = await apiService.postInfoGetlinkUrl({ page: [LinkPositionEnum.Footer] });
      if (response?.footer) setFooterState(response.footer);
    })();
  }, []);

  const handleRedirect = (info: LinkInfoResp) => {
    if (info.link) {
      if (info.login === '1' && !authorization) {
        modalService.trigger(ModalNamesEnum.MemberLoginModal, true);
        return;
      }
      if (info.target === '1') {
        window.open(info.link, '_blank');
        return;
      }
      window.location.replace(info.link);
      return;
    }
    if (info.doc) {
      const docURLParams = commonBaseUrlsState.find((param) => param.paramId === SystemParamIdEnum.DocURL);
      if (docURLParams) {
        const url = `${docURLParams.paramValue}${info.doc}`;
        info.target === '1' ? window.open(url, '_blank') : window.location.replace(url);
      }
    }
  };

  /**
   * @description 根據 HM1240404E、GN1320200E 準則，組合標題文字
   * @param info 連結資訊
   */
  const getTitle = (info: LinkInfoResp) => {
    let title = '';
    if (info.link) title = '另開新視窗';
    if (info.type === LinkTypesEnum.Doc) {
      // 若是 pdf，則顯示文件名
      title = `開啟「${info.doc}」`;
    }
    return title;
  };

  return (
    <footer>
      <div className="container d-flex">
        <div className="d-flex groups-wrapper">
          {footerNavs.map((nav, index) => (
            <div className="group" key={nav.id}>
              {index === 0 && (
                /* eslint-disable jsx-a11y/no-access-key */
                /* 避免畫面閃爍，故設定 preventDefault() */
                <a tabIndex={isModalShown ? -1 : 0} id="footer-content" href="#footer-content" accessKey="Z" className="access-brick-white" title="下方導覽連結區：ALT+Z" onClick={(e) => e.preventDefault()}>:::</a>
              )}
              <div className="subject">
                <span>{nav.text}</span>
                {/* GN1210100E (鍵盤可操作) */}
                <span
                  id={`${nav.id}-${index}`}
                  tabIndex={isModalShown ? -1 : 0}
                  className="symbol add"
                  onKeyDown={(e) => {
                    if (e.code === 'Enter') {
                      const symbol = $(`#${nav.id}-${index}`);
                      if (symbol) {
                        symbol.toggleClass('minus');
                        symbol.toggleClass('add');
                        symbol.parents().siblings('.footer-items').slideToggle();
                      }
                    }
                  }}
                />
              </div>
              {nav.child && (
                <div className="footer-items">
                  <ul>
                    {/* GN1210100E (鍵盤可操作) */}
                    {nav.child.map((subNav) => (
                      <li title={getTitle(subNav)} key={subNav.id}>
                        <a tabIndex={isModalShown ? -1 : 0} href="/#" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleRedirect(subNav); }}>{subNav.text}</a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="right">
          {/* 加入無障礙AA標章 */}
          <a className="AA-logo" tabIndex={isModalShown ? -1 : 0} href="https://accessibility.moda.gov.tw/Applications/Detail?category=20241125114333&quot" title="無障礙網站">
            <img style={{ border: 0, width: 88, height: 31 }} src={require('assets/img/icons/AA-logo.png')} alt="通過AA無障礙網頁檢測" />
          </a>
          {footerInfoLinks.length > 0 && (
            <div>
              {/* GN1210100E (鍵盤可操作) */}
              {footerInfoLinks.map((info, index) => (
                <span title="另開新視窗" key={info.id} className={index > 0 ? 'ml-1' : ''}>
                  <a tabIndex={isModalShown ? -1 : 0} href="/#" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleRedirect(info); }}>{info.text}</a>
                </span>
              ))}
            </div>
          )}
          {footerInfoText.map((info) => (
            <div key={info.id}>
              {info.text}
              {info.id === FooterLinkIdEnum.Browser && (
                <>
                  <span>
                    <img src={require('assets/img/icons/edge.svg').default} alt="" />
                    Edge 91+、
                  </span>
                  <span>
                    <img src={require('assets/img/icons/chrome.svg').default} alt="" />
                    Chrome 86+、
                  </span>
                  <span>
                    <img src={require('assets/img/icons/firefox.svg').default} alt="" />
                    Firefox 88+、
                  </span>
                  <span>
                    <img src={require('assets/img/icons/safari.svg').default} alt="" />
                    Safari 12+
                  </span>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
